import { useTranslation } from 'react-i18next';
import { useFreeAccess } from 'features/subscription/hooks/use-seats';
import { useSubscriptionUsage, useWindowLimit } from 'features/usage/hooks/use-usage';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { AI_CREDITS_METER } from '../constants/credits';
import { useCreditSummary, useTopUpCoversUsage } from './use-credits';

// The server accepts at most 60 characters, and a limit's name is typed by an admin.
const MAX_LIMIT_NAME_LENGTH = 60;

/**
 * Set once a daily/hourly window or the month's credits are used up and top-up credits do not
 * cover it. It names the full limit that resets last, so it is also when work can go on.
 */
export const useUsageLimitReached = (): { limitName: string; resetsAt?: string } | null => {
  const { t } = useTranslation();
  const userId = useCommonStore((state) => state.user?.id) ?? '';
  const topUpCovers = useTopUpCoversUsage();
  const { isLoading: isCreditsLoading } = useCreditSummary();
  const { isFreeAccess, isLoading: isFreeAccessLoading } = useFreeAccess();
  const { data: windowLimit } = useWindowLimit(userId);
  const { data: meters } = useSubscriptionUsage();

  const fullWindows = (windowLimit?.windows ?? []).filter((window) => window.percent >= 100);
  const fullMeters = (meters ?? []).filter(
    (meter) =>
      meter.meterKey === AI_CREDITS_METER && meter.included > 0 && meter.used >= meter.included
  );

  // With enforcement off nothing is limited, whatever an old meter still says.
  if (isCreditsLoading || isFreeAccessLoading || isFreeAccess || topUpCovers) return null;
  if (!fullWindows.length && !fullMeters.length) return null;

  const fullLimits = [
    ...fullWindows.map((window) => ({
      name: window.name || window.key,
      resetsAt: window.resets_at,
    })),
    ...fullMeters.map((meter) => ({
      name: t('MONTHLY_LIMIT', 'monthly limit'),
      resetsAt: meter.periodEndUtc,
    })),
  ].map((limit) => ({ ...limit, resetTime: Date.parse(limit.resetsAt ?? '') }));

  const known = fullLimits.filter((limit) => !Number.isNaN(limit.resetTime));
  const last = known.length
    ? known.reduce((latest, limit) => (limit.resetTime > latest.resetTime ? limit : latest))
    : fullLimits[0];

  return {
    limitName: last.name.slice(0, MAX_LIMIT_NAME_LENGTH),
    resetsAt: known.length ? new Date(last.resetTime).toISOString() : undefined,
  };
};
